// src/routes/merchants/picker/useMerchantPicker.ts
import { useInfiniteQuery, useQuery, type InfiniteData } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../../../api/client'
import { keys } from '../../../api/keys'
import type { components } from '../../../api/generated/schema'
import type { AsyncState } from '../../../platform/async/Async'
import { fromQuery } from '../../../platform/async/fromQuery'
import { MerchantsError } from '../useMerchants'
import { MOST_USED_MAX, mergeMerchants, toPickerMerchant, type PickerMerchant } from './merchantPickerModel'

/** One A–Z or search page: the server's 100 by default, of its 1–500. */
export const DIRECTORY_PAGE_SIZE = 100

/**
 * `GET /merchants?sort=most_used`: the server counts the caller's own live transactions per merchant and
 * orders by that count. One page of `MOST_USED_MAX`. Soft-deleted merchants are not listed (no
 * `include_inactive`), so a deleted merchant is never a tile and needs no by-id check. Merchants the user
 * never used are still listed, last, with a count of 0 (paging stays complete); they are not "most used".
 */
async function fetchMostUsed(signal: AbortSignal): Promise<PickerMerchant[]> {
  const { data, error, response } = await api.GET('/api/v1/merchants', {
    params: { query: { sort: 'most_used', page: 1, page_size: MOST_USED_MAX, include_global: true } },
    signal,
  })
  if (error !== undefined || data === undefined) throw new MerchantsError(error, response.status)
  return data.merchants.filter((m) => (m.transaction_count ?? 0) > 0).map(toPickerMerchant)
}

export type MostUsedMerchants = {
  /** False until the first answer (or failure) lands. The picker shows the section's own placeholder meanwhile; it never waits on it. */
  settled: boolean
  merchants: readonly PickerMerchant[]
}

const NONE: readonly PickerMerchant[] = []

/**
 * The picker's "Most used": up to eight merchants, by how many of the user's own transactions carry each,
 * as the server counts them.
 *
 * A failure settles empty and is not retried: the section is a convenience, so it is simply absent, and the
 * A–Z list and search never wait for it.
 *
 * **Not cached.** The backend computes usage per request and says it must not be cached, so `gcTime` is 0:
 * each open of the picker asks again, and the tiles' placeholder shows until it answers.
 */
export function useMostUsedMerchants(): MostUsedMerchants {
  const query = useQuery({
    queryKey: keys.merchantUsage.mostUsed(),
    queryFn: ({ signal }) => fetchMostUsed(signal),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })
  const settled = query.data !== undefined || query.isError
  const merchants = query.data ?? NONE
  return useMemo(() => ({ settled, merchants }), [settled, merchants])
}

type MerchantList = components['schemas']['MerchantList']
export type DirectoryPage = { merchants: PickerMerchant[]; total: number }
/** `idle` is a search with nothing to search for: no request, nothing to show. */
export type DirectoryState = AsyncState<DirectoryPage> | { status: 'idle' }

/**
 * Pages → one list, each merchant once. Offset paging over `is_global, name` (no id tiebreak) can repeat
 * a row across a page boundary, and so can a merchant created or renamed mid-scroll; the first sighting wins.
 */
const flatten = (data: InfiniteData<MerchantList, number>): DirectoryPage => ({
  merchants: mergeMerchants(...data.pages.map((p) => p.merchants.map(toPickerMerchant))),
  total: data.pages[0]?.total ?? 0,
})

/**
 * `GET /merchants`, paged — the A–Z browse when `search` is blank, a server search otherwise. Global
 * merchants are included (the server's default, sent explicitly). The server orders by `is_global`, then
 * name: the user's own merchants A–Z, then the shared ones A–Z.
 *
 * **Never assumes the list fits.** Pages accumulate through `loadMore`; a user who can see more than
 * one page finds the rest by scrolling, "Show more", or a search, which reaches the server rather than
 * filtering what happens to be loaded.
 *
 * The caller debounces `search`; this hook sends whatever it is given.
 */
export function useMerchantDirectory(
  search: string,
  { enabled = true }: { enabled?: boolean } = {},
): { state: DirectoryState; hasMore: boolean; loadingMore: boolean; loadMore: () => void } {
  const term = search.trim()
  const result = useInfiniteQuery({
    queryKey: keys.merchants.picker(term),
    queryFn: async ({ pageParam, signal }): Promise<MerchantList> => {
      const { data, error, response } = await api.GET('/api/v1/merchants', {
        params: {
          query: { ...(term === '' ? {} : { search: term }), page: pageParam, page_size: DIRECTORY_PAGE_SIZE, include_global: true },
        },
        signal,
      })
      if (error !== undefined || data === undefined) throw new MerchantsError(error, response.status)
      return data
    },
    initialPageParam: 1,
    getNextPageParam: (last, _all, lastParam) => (last.has_more ? lastParam + 1 : undefined),
    select: flatten,
    enabled,
  })
  const state = fromQuery(result, {
    message: () => (term === '' ? 'Merchants could not be loaded.' : 'The merchant search failed.'),
    recovery: { label: 'Try again', onAct: () => void result.refetch() },
  })
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = result
  const loadMore = useCallback(() => {
    // Guarded here, not trusted to the caller: a scroll sentinel and a button can both ask at once.
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])
  return { state: enabled ? state : { status: 'idle' }, hasMore: enabled && hasNextPage, loadingMore: isFetchingNextPage, loadMore }
}

/** The search box's value, settled for `ms` — what the server is asked about. */
export function useDebouncedValue<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    if (Object.is(value, settled)) return
    const id = setTimeout(() => setSettled(value), ms)
    return () => clearTimeout(id)
  }, [value, settled, ms])
  return settled
}
